import { format, parseISO } from 'date-fns';
import { Carrier } from 'types';
import { Color } from 'app/models/Color';
import { Destination } from 'app/models/Destination';
import { PartOrderJson, createPartOrder } from 'app/models/PartOrder';
import { PackageOrder, PackageOrderJson } from 'app/models/PackageOrder';
import { Tour } from 'app/models/Tour';

export type ShopPackagesPrintStatus = 'pending' | 'done' | 'neutral';

export interface PrintPartOrderApiResponse extends PartOrderJson {
  delivered?: number | boolean;
}

export interface ShopPackageSheetPackage {
  id: number;
  formattedId: string;
  name: string;
  dimensionsText: string;
}

export interface ShopPackageSheetOrder {
  partOrderId: number;
  orderId: number;
  idWithPart: string;
  customer: string;
  customerLastname: string;
  company: string;
  date: string;
  formattedDate: string;
  partial: boolean;
  paid: boolean;
  packageCount: number;
  packages: ShopPackageSheetPackage[];
  status: ShopPackagesPrintStatus;
}

export interface ShopPackagesPrintModel {
  title: string;
  date: string;
  formattedDate: string;
  shopLg: 'fr' | 'nl';
  printPickupTable: boolean;
  orders: ShopPackageSheetOrder[][];
  pendingOrders: ShopPackageSheetOrder[];
}

interface BuildModelParams {
  tour: Tour;
  currentPartOrders: PrintPartOrderApiResponse[];
  excludedPartOrders: PrintPartOrderApiResponse[];
  destinations: Destination[];
  carriers: Carrier[];
  colors: Color[];
}

function formatPrintDate(date: string) {
  try {
    return format(parseISO(date), 'dd/MM/yyyy');
  } catch {
    return date;
  }
}

function getStatusFromDelivered(
  delivered: PrintPartOrderApiResponse['delivered'],
): ShopPackagesPrintStatus {
  if (typeof delivered === 'number') {
    if (delivered < 0) return 'pending';
    if (delivered > 0) return 'done';
    return 'neutral';
  }

  if (delivered === true) return 'done';

  return 'neutral';
}

function formatPackageId(packageId: number) {
  const padded = `${packageId}`.padStart(9, '0');
  return `${padded.slice(0, 3)}.${padded.slice(3, 6)}.${padded.slice(6, 9)}`;
}

function truncate(value: string, length: number) {
  if (value.length <= length) return value;
  return `${value.slice(0, Math.max(length - 3, 0))}...`;
}

function buildCustomer(firstname?: string, lastname?: string) {
  const firstInitial = firstname?.trim().charAt(0).toUpperCase();
  const upperLastname = lastname?.trim().toUpperCase();
  return [firstInitial, upperLastname].filter(Boolean).join(' ');
}

function buildCustomerLastname(firstname?: string, lastname?: string) {
  const firstInitial = firstname?.trim().charAt(0).toUpperCase();
  const upperLastname = lastname?.trim().toUpperCase();
  return [upperLastname, firstInitial].filter(Boolean).join(' ');
}

function getPackageLabel(pkg: PackageOrder) {
  return (
    pkg.name ??
    pkg.uniqueDescr ??
    pkg.description ??
    `Package #${pkg.packageNumber}`
  );
}

function mapPackages(packages?: PackageOrder[]): ShopPackageSheetPackage[] {
  return (packages ?? []).map(pkg => ({
    id: pkg.id,
    formattedId: formatPackageId(pkg.id),
    name: getPackageLabel(pkg),
    dimensionsText: pkg.dimensions ? `(${pkg.dimensions}cm)` : '',
  }));
}

function mapApiPackages(
  packages?: PackageOrderJson[],
): ShopPackageSheetPackage[] {
  return (packages ?? []).map(pkg => ({
    id: pkg.id,
    formattedId: formatPackageId(pkg.id),
    name: pkg.name ?? `Package #${pkg.id}`,
    dimensionsText: pkg.dimensions ? `(${pkg.dimensions}cm)` : '',
  }));
}

function mapOrder(
  partOrder: ReturnType<typeof createPartOrder>,
  status: ShopPackagesPrintStatus,
  apiPackages?: PackageOrderJson[],
): ShopPackageSheetOrder {
  const firstname = partOrder.orderInfo.firstname;
  const lastname = partOrder.orderInfo.lastname;
  const packages = apiPackages
    ? mapApiPackages(apiPackages)
    : mapPackages(partOrder.packages);

  return {
    partOrderId: partOrder.id,
    orderId: partOrder.order.id,
    idWithPart:
      partOrder.total > 1
        ? `${partOrder.order.id} (${partOrder.index})`
        : `${partOrder.order.id}`,
    customer: buildCustomer(firstname, lastname),
    customerLastname: buildCustomerLastname(firstname, lastname),
    company: partOrder.orderInfo.company ?? '',
    date: partOrder.orderInfo.invoiceDate,
    formattedDate: formatPrintDate(partOrder.orderInfo.invoiceDate),
    partial: partOrder.total > 1,
    paid: Boolean(partOrder.orderInfo.paid),
    packageCount: packages.length,
    packages,
    status,
  };
}

function buildPickupLabel(order: ShopPackageSheetOrder) {
  const company = order.company ? `, ${order.company.toUpperCase()}` : '';
  return truncate(`${order.customerLastname}${company}`, 30);
}

export function getPickupDisplayName(order: ShopPackageSheetOrder) {
  return buildPickupLabel(order);
}

export function buildPendingSummaryLabel(order: ShopPackageSheetOrder) {
  const company = order.company ? `, ${order.company.toUpperCase()}` : '';
  return `${order.customer}${company} (${order.idWithPart})`;
}

export function buildShopPackagesPrintModel({
  tour,
  currentPartOrders,
  excludedPartOrders,
  destinations,
  carriers,
  colors,
}: BuildModelParams): ShopPackagesPrintModel {
  const includedOrders = currentPartOrders.map(entry => {
    const partOrder = createPartOrder(entry, destinations, carriers, colors);
    return mapOrder(partOrder, 'pending', entry.packages);
  });

  const excludedOrders = excludedPartOrders.map(entry => {
    const partOrder = createPartOrder(entry, destinations, carriers, colors);
    return mapOrder(
      partOrder,
      getStatusFromDelivered(entry.delivered),
      entry.packages,
    );
  });

  const deduped = new Map<number, ShopPackageSheetOrder>();

  [...includedOrders, ...excludedOrders].forEach(order => {
    if (!deduped.has(order.partOrderId)) {
      deduped.set(order.partOrderId, order);
    }
  });

  const allOrders = Array.from(deduped.values()).sort((left, right) => {
    if (left.orderId === right.orderId) {
      return left.idWithPart.localeCompare(right.idWithPart, undefined, {
        numeric: true,
      });
    }

    return left.orderId - right.orderId;
  });

  const grouped = new Map<number, ShopPackageSheetOrder[]>();

  allOrders.forEach(order => {
    const current = grouped.get(order.orderId) ?? [];
    current.push(order);
    grouped.set(order.orderId, current);
  });

  return {
    title: tour.title,
    date: tour.date,
    formattedDate: formatPrintDate(tour.date),
    shopLg: tour.destination?.shopLg === 'nl' ? 'nl' : 'fr',
    printPickupTable: Boolean(tour.destination?.shop && !tour.isReseller),
    orders: Array.from(grouped.values()),
    pendingOrders: allOrders.filter(order => order.status === 'pending'),
  };
}
